export type ToolId =
  | "bin-lookup"
  | "iban-validation"
  | "profanity-detection"
  | "ip-lookup"
  | "proxy-vpn-check"
  | "asn-lookup"
  | "disposable-email"
  | "domain-scoring"
  | "phone-validation";

export type ToolMode = "path" | "turnstile";

export type ToolDefinition = {
  id: ToolId;
  /** Canonical lowercase URL segment under `/tools/`. */
  slug: string;
  /** Legacy PHP path segments (mixed case). Used only for redirects. */
  legacySlugs: string[];
  mode: ToolMode;
  inputType: "text" | "email" | "textarea";
  inputName: string;
  pathEncode?: boolean;
  autoLookup?: boolean;
};

export const TOOL_DEFINITIONS: ToolDefinition[] = [
  {
    id: "bin-lookup",
    slug: "bin-lookup",
    legacySlugs: ["BIN-Lookup"],
    mode: "path",
    inputType: "text",
    inputName: "bin",
  },
  {
    id: "iban-validation",
    slug: "iban-validation",
    legacySlugs: ["IBAN-Validation"],
    mode: "turnstile",
    inputType: "text",
    inputName: "iban",
  },
  {
    id: "profanity-detection",
    slug: "profanity-detection",
    legacySlugs: ["Profanity-Detection"],
    mode: "turnstile",
    inputType: "textarea",
    inputName: "text",
  },
  {
    id: "ip-lookup",
    slug: "what-is-my-ip-address",
    legacySlugs: ["What-Is-My-IP-Address"],
    mode: "path",
    inputType: "text",
    inputName: "ip",
    autoLookup: true,
  },
  {
    id: "proxy-vpn-check",
    slug: "proxy-vpn-checker",
    legacySlugs: [],
    mode: "path",
    inputType: "text",
    inputName: "ip",
    autoLookup: true,
  },
  {
    id: "asn-lookup",
    slug: "asn-lookup",
    legacySlugs: ["ASN-Lookup"],
    mode: "path",
    inputType: "text",
    inputName: "asn",
  },
  {
    id: "disposable-email",
    slug: "email-scoring",
    legacySlugs: [
      "Email-Scoring",
      "disposable-email-checker",
      "Disposable-Email-Checker",
    ],
    mode: "path",
    inputType: "email",
    inputName: "email",
    pathEncode: true,
  },
  {
    id: "domain-scoring",
    slug: "domain-scoring",
    legacySlugs: ["Domain-Scoring"],
    mode: "path",
    inputType: "text",
    inputName: "domain",
  },
  {
    id: "phone-validation",
    slug: "phone-number-validator",
    legacySlugs: [],
    mode: "path",
    inputType: "text",
    inputName: "phone",
    pathEncode: true,
  },
];

const slugToTool = new Map<string, ToolDefinition>();

for (const tool of TOOL_DEFINITIONS) {
  slugToTool.set(tool.slug, tool);
  for (const legacySlug of tool.legacySlugs) {
    slugToTool.set(legacySlug.toLowerCase(), tool);
  }
}

export function getToolBySlug(slug: string): ToolDefinition | undefined {
  return slugToTool.get(slug.toLowerCase());
}

export const TOOL_EXAMPLES: Record<ToolId, string[]> = {
  "bin-lookup": ["424242", "453201", "541333"],
  "iban-validation": ["GB82WEST12345698765432", "DE89370400440532013000"],
  "profanity-detection": [
    "Hello, welcome to our platform!",
    "Thanks for your order.",
  ],
  "ip-lookup": ["8.8.8.8", "1.1.1.1", "208.67.222.222"],
  "proxy-vpn-check": ["8.8.8.8", "1.1.1.1", "208.67.222.222"],
  "asn-lookup": ["AS15169", "AS13335", "AS8075"],
  "disposable-email": ["user@gmail.com", "test@mailinator.com"],
  "domain-scoring": ["google.com", "example.com", "github.com"],
  "phone-validation": ["US~2125551234", "GB~7911123456", "DE~15123456789"],
};

export function getToolPath(tool: ToolDefinition, query?: string) {
  if (!query) {
    return `/tools/${tool.slug}`;
  }

  const value = tool.pathEncode ? encodeURIComponent(query) : query;
  return `/tools/${tool.slug}/${value}`;
}
